/**
 * RestaurantMapView - Inline Map replacing List View
 * 
 * NOT a fullscreen overlay - replaces the restaurant list area
 * while keeping header/filters intact.
 * 
 * Features:
 * - Marker clustering for grouped restaurants
 * - Pin click → reel scrolls to and highlights the exact restaurant
 * - Restaurants sorted by distance from user location
 * - Tap empty map area OR X button to hide carousel
 * - Click card to open restaurant detail page
 * - "Speisekarte" button on each reel card
 * - Desktop: mouse drag scroll for carousel with momentum
 * - Glowing green user location marker
 * - SpeiseGuru pepper mill icon on markers
 * - Logo avatar overlay on card images
 * - Blinking green dot for open status
 * - Mobile-optimized touch interactions
 */
import { memo, useState, useEffect, useRef, useCallback, useMemo, forwardRef } from 'react';
import { ChevronLeft, ChevronRight, Clock, Bike, Navigation, X, List, ShoppingBag, BookOpen } from 'lucide-react';
import { cn } from '@/lib/utils';
import { slugify } from '@shared/utils';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet.markercluster';
import 'leaflet.markercluster/dist/MarkerCluster.css';
import 'leaflet.markercluster/dist/MarkerCluster.Default.css';
import type { RestaurantWithDetails } from '@/types/search';
import { isRestaurantOpenNow, getNextOpeningTime } from '@/utils/restaurantTimes';

// Fix Leaflet default marker icon
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';

// @ts-ignore
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconUrl: markerIcon,
  iconRetinaUrl: markerIcon2x,
  shadowUrl: markerShadow,
});

// Helper to normalize image URLs
function getImageUrl(path: string | undefined): string {
  if (!path) return '';
  if (path.startsWith('http')) return path;
  if (path.startsWith('/')) return path;
  return `/images/${path}`;
}

// Haversine distance in km
function haversineDistance(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const R = 6371;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLng = (lng2 - lng1) * Math.PI / 180;
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

// Placeholder image for broken/missing images
const PLACEHOLDER_IMAGE = 'data:image/svg+xml,' + encodeURIComponent(`
<svg xmlns="http://www.w3.org/2000/svg" width="400" height="200" viewBox="0 0 400 200">
  <rect fill="#e5e7eb" width="400" height="200"/>
  <g transform="translate(175, 70)">
    <rect fill="#9ca3af" x="5" y="0" width="40" height="60" rx="4"/>
    <circle fill="#d1d5db" cx="25" cy="20" r="12"/>
    <rect fill="#d1d5db" x="15" y="38" width="20" height="4" rx="2"/>
    <rect fill="#d1d5db" x="18" y="46" width="14" height="3" rx="1"/>
  </g>
</svg>
`);

// Logo placeholder
const LOGO_PLACEHOLDER = 'data:image/svg+xml,' + encodeURIComponent(`
<svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 48 48">
  <rect fill="#f3f4f6" width="48" height="48" rx="8"/>
  <path fill="#9ca3af" d="M24 14c-5.5 0-10 4.5-10 10s4.5 10 10 10 10-4.5 10-10-4.5-10-10-10zm0 18c-4.4 0-8-3.6-8-8s3.6-8 8-8 8 3.6 8 8-3.6 8-8 8z"/>
  <circle fill="#9ca3af" cx="24" cy="24" r="3"/>
</svg>
`);

interface RestaurantMapViewProps {
  restaurants: RestaurantWithDetails[];
  userLocation?: { lat: number; lng: number } | null;
  onRestaurantClick?: (restaurant: RestaurantWithDetails) => void;
  onBackToList?: () => void;
  className?: string;
}

// SpeiseGuru Pepper Mill SVG icon for markers (matches the logo)
const PEPPER_MILL_SVG = (size: number) => `
  <svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
    <path d="M12 2v1"/>
    <path d="M10 3h4"/>
    <path d="M9 4h6a1 1 0 0 1 1 1v2a3 3 0 0 1-3 3h-2a3 3 0 0 1-3-3V5a1 1 0 0 1 1-1z"/>
    <path d="M9 10h6"/>
    <rect x="9" y="10" width="6" height="10" rx="1"/>
    <path d="M12 20v2"/>
    <path d="M10 14h4"/>
    <path d="M10 17h4"/>
  </svg>
`;

// Custom marker icons - SpeiseGuru Green with pepper mill
const createMarkerIcon = (isActive: boolean) => {
  const size = isActive ? 48 : 38;
  const bgColor = isActive ? '#007538' : '#009544';
  const iconSize = isActive ? 22 : 18;
  
  return L.divIcon({
    className: 'custom-marker',
    html: `
      <div style="
        width: ${size}px;
        height: ${size}px;
        background: ${bgColor};
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        color: white;
        box-shadow: 0 4px 14px rgba(0,149,68,${isActive ? '0.7' : '0.5'});
        border: 3px solid white;
        transition: transform 0.2s ease, box-shadow 0.2s ease;
        cursor: pointer;
        ${isActive ? 'transform: scale(1.15); z-index: 1000;' : ''}
      ">
        ${PEPPER_MILL_SVG(iconSize)}
      </div>
    `,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
  });
};

// User location marker - Glowing green
const createUserLocationIcon = () => {
  return L.divIcon({
    className: 'user-location-marker',
    html: `
      <div style="position: relative; width: 24px; height: 24px;">
        <div style="
          position: absolute;
          inset: 0;
          background: rgba(0, 149, 68, 0.3);
          border-radius: 50%;
          animation: pulse-ring 2s ease-out infinite;
        "></div>
        <div style="
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          width: 14px;
          height: 14px;
          background: #009544;
          border-radius: 50%;
          border: 3px solid white;
          box-shadow: 0 2px 8px rgba(0,149,68,0.5);
        "></div>
      </div>
      <style>
        @keyframes pulse-ring {
          0% { transform: scale(0.8); opacity: 1; }
          100% { transform: scale(2); opacity: 0; }
        }
      </style>
    `,
    iconSize: [24, 24],
    iconAnchor: [12, 12],
  });
};

// Restaurant Card Component for Map Reel
const RestaurantMapCard = memo(forwardRef<HTMLDivElement, {
  restaurant: RestaurantWithDetails;
  isActive: boolean;
  distanceKm?: number;
  onSelect: () => void;
  onOpenDetail: () => void;
  onOpenMenu: () => void;
}>(function RestaurantMapCard({
  restaurant,
  isActive,
  distanceKm,
  onSelect,
  onOpenDetail,
  onOpenMenu,
}, ref) {
  const [imageError, setImageError] = useState(false);
  const [logoError, setLogoError] = useState(false);
  
  // Get first non-menu image, or first image as fallback
  const primaryImage = restaurant.images?.find(img => img.is_menu === false)?.path 
    || restaurant.images?.[0]?.path;
  const logoImage = restaurant.images?.find(img => img.is_menu === false)?.path || primaryImage;
  
  const { isOpen } = isRestaurantOpenNow(restaurant.openingHours || []);
  
  // Get next opening time for closed restaurants
  const nextOpeningRaw = !isOpen ? getNextOpeningTime(restaurant.openingHours || []) : null;
  const nextOpeningTime = nextOpeningRaw ? nextOpeningRaw.replace(' Uhr', '').split(', ').pop() : null;
  
  const deliveryTime = restaurant.deliveryTimeMin && restaurant.deliveryTimeMax 
    ? `${restaurant.deliveryTimeMin}-${restaurant.deliveryTimeMax}`
    : restaurant.deliveryTimeMin 
      ? `${restaurant.deliveryTimeMin}`
      : null;
  const cuisineNames = restaurant.categories?.slice(0, 2).map(c => c.name).join(', ') || '';
  
  const displayDistance = distanceKm ?? restaurant.distanceKm;
  
  // Build image URLs with proper fallbacks
  const imageSrc = imageError || !primaryImage ? PLACEHOLDER_IMAGE : getImageUrl(primaryImage);
  const logoSrc = logoError || !logoImage ? LOGO_PLACEHOLDER : getImageUrl(logoImage);
  
  return (
    <div
      ref={ref}
      className={cn(
        "flex-shrink-0 w-[240px] sm:w-[260px] bg-white rounded-xl relative overflow-hidden",
        "transition-all duration-300 snap-start cursor-pointer border",
        isActive 
          ? "ring-2 ring-[#009544] shadow-md scale-[1.02] border-[#009544]/20" 
          : "shadow-sm hover:shadow-md border-gray-100"
      )}
      style={{
        marginBottom: '8px'
      }}
      onClick={onSelect}
    >
      {/* Closed Banner - Top bar inside card */}
      {!isOpen && nextOpeningTime && (
        <div className="w-full bg-gray-800 text-white text-[10px] font-medium text-center py-1.5">
          Geöffnet ab {nextOpeningTime}
        </div>
      )}
      
      {/* Image */}
      <div className={cn(
        "relative bg-gray-200 overflow-hidden",
        !isOpen && nextOpeningTime ? "h-24 rounded-none" : "h-28 rounded-t-xl"
      )}>
        <img 
          src={imageSrc} 
          alt={restaurant.name}
          className="w-full h-full object-cover"
          loading="lazy"
          decoding="async"
          onError={() => setImageError(true)}
        />
        
        {/* Open Status - Blinking Green Dot */}
        {(isOpen || !nextOpeningTime) && (
          <div className="absolute top-2 right-2 flex items-center gap-1.5 px-2 py-1 rounded-full bg-white/90 backdrop-blur-sm">
            {isOpen ? (
              <>
                <span className="relative flex h-2.5 w-2.5">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#009544] opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-[#009544]"></span>
                </span>
                <span className="text-[10px] font-medium text-[#009544]">Offen</span>
              </>
            ) : (
              <>
                <span className="h-2.5 w-2.5 rounded-full bg-gray-400"></span>
                <span className="text-[10px] font-medium text-gray-500">Zu</span>
              </>
            )}
          </div>
        )}
        
        {/* Distance badge on image - top left */}
        {displayDistance != null && (
          <div className="absolute top-2 left-2 flex items-center gap-1 px-2 py-1 rounded-full bg-black/60 text-white text-[10px] font-medium backdrop-blur-sm">
            <Navigation className="w-2.5 h-2.5" />
            {displayDistance.toFixed(1)} km
          </div>
        )}
      </div>
      
      {/* Logo Avatar */}
      <div 
        className="absolute left-3 w-11 h-11 rounded-lg bg-white overflow-hidden border-2 border-white z-10"
        style={{ 
          boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
          top: !isOpen && nextOpeningTime ? 'calc(6rem + 24px - 1.25rem)' : 'calc(7rem - 1.25rem)'
        }}
      >
        <img 
          src={logoSrc}
          alt={`${restaurant.name} Logo`}
          className="w-full h-full object-cover"
          loading="lazy"
          onError={() => setLogoError(true)}
        />
      </div>
      
      {/* Content */}
      <div className="p-3 pt-7">
        <div className="flex items-start justify-between gap-1.5">
          <h3 className="font-semibold text-gray-900 text-sm truncate flex-1">{restaurant.name}</h3>
        </div>
        
        <p className="text-[11px] text-gray-500 truncate mt-0.5">{cuisineNames}</p>
        
        <div className="flex items-center gap-2 mt-1.5 text-[11px] text-gray-600">
          {deliveryTime && (
            <div className="flex items-center gap-0.5">
              <Clock className="w-3 h-3" />
              <span>{deliveryTime} Min</span>
            </div>
          )}
          
          {/* Delivery/Pickup Badges */}
          <div className="flex items-center gap-1 text-[#009544]">
            {(restaurant.delivery || (restaurant as any).has_delivery) && (
              <Bike className="w-3 h-3" />
            )}
            {((restaurant as any).pickup || (restaurant as any).has_pickup || !(restaurant.delivery || (restaurant as any).has_delivery)) && (
              <ShoppingBag className="w-3 h-3" />
            )}
          </div>
        </div>
        
        {/* Speisekarte Button */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            onOpenMenu();
          }}
          className="w-full flex items-center justify-center gap-1.5 px-2.5 py-1.5 mt-2.5 rounded-lg border border-[#009544]/30 text-[#009544] text-[11px] font-normal hover:bg-[#009544]/5 active:scale-[0.97] transition-all touch-manipulation"
        >
          <BookOpen className="w-3 h-3" />
          <span>Speisekarte</span>
        </button>
      </div>
    </div>
  );
}));

export const RestaurantMapView = memo(function RestaurantMapView({
  restaurants,
  userLocation,
  onRestaurantClick,
  onBackToList,
  className
}: RestaurantMapViewProps) {
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<L.Map | null>(null);
  const markersRef = useRef<L.Marker[]>([]);
  const clusterGroupRef = useRef<L.MarkerClusterGroup | null>(null);
  const userMarkerRef = useRef<L.Marker | null>(null);
  const cardsContainerRef = useRef<HTMLDivElement>(null);
  const cardRefsMap = useRef<Map<string, HTMLDivElement>>(new Map());
  
  const [activeRestaurantId, setActiveRestaurantId] = useState<string | null>(null);
  const [showCarousel, setShowCarousel] = useState(false);
  const [mapReady, setMapReady] = useState(false);
  
  // Mouse drag scroll for desktop with momentum
  const isDraggingRef = useRef(false);
  const dragStartRef = useRef({ x: 0, scrollLeft: 0, lastX: 0, lastTime: 0, velocity: 0 });
  const momentumRef = useRef<number | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  
  // Sort restaurants by distance from user location for consistent ordering
  const sortedRestaurants = useMemo(() => {
    const valid = restaurants.filter(r => {
      const lat = Number(r.latitude);
      const lng = Number(r.longitude);
      return !isNaN(lat) && !isNaN(lng) && lat !== 0 && lng !== 0;
    });
    
    if (!userLocation) return valid;
    
    return [...valid].sort((a, b) => {
      const distA = haversineDistance(userLocation.lat, userLocation.lng, Number(a.latitude), Number(a.longitude));
      const distB = haversineDistance(userLocation.lat, userLocation.lng, Number(b.latitude), Number(b.longitude));
      return distA - distB;
    });
  }, [restaurants, userLocation]);
  
  // Computed distances 
  const restaurantDistances = useMemo(() => {
    if (!userLocation) return new Map<string, number>();
    const map = new Map<string, number>();
    sortedRestaurants.forEach(r => {
      map.set(r.id, haversineDistance(userLocation.lat, userLocation.lng, Number(r.latitude), Number(r.longitude)));
    });
    return map;
  }, [sortedRestaurants, userLocation]);
  
  // Build a fast ID→index map for O(1) lookups  
  const idToIndexMap = useMemo(() => {
    const map = new Map<string, number>();
    sortedRestaurants.forEach((r, i) => map.set(r.id, i));
    return map;
  }, [sortedRestaurants]);
  
  // Get active index from ID
  const activeIndex = activeRestaurantId ? (idToIndexMap.get(activeRestaurantId) ?? null) : null;
  
  // Initialize map
  useEffect(() => {
    if (!mapContainerRef.current || mapRef.current) return;
    
    let center: [number, number] = [51.1657, 10.4515];
    let zoom = 6;
    
    if (userLocation) {
      center = [userLocation.lat, userLocation.lng];
      zoom = 13;
    } else if (sortedRestaurants.length > 0) {
      center = [Number(sortedRestaurants[0].latitude), Number(sortedRestaurants[0].longitude)];
      zoom = 13;
    }
    
    const map = L.map(mapContainerRef.current, {
      center,
      zoom,
      zoomControl: false,
      attributionControl: false, // Hide attribution on mobile for clean look
      tap: true, // Enable tap for mobile
      tapTolerance: 15, // Increase tap tolerance for mobile
      touchZoom: true,
      bounceAtZoomLimits: false,
    });
    
    // Minimal attribution
    L.control.attribution({ position: 'bottomleft', prefix: false }).addTo(map);
    
    // Use CartoDB Voyager tiles - clean and reliable
    L.tileLayer('https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png', {
      maxZoom: 19,
      subdomains: 'abcd',
      attribution: '© <a href="https://www.openstreetmap.org/copyright">OSM</a> © <a href="https://carto.com/">CARTO</a>'
    }).addTo(map);
    
    // Zoom control top-right, not on mobile
    if (window.innerWidth >= 768) {
      L.control.zoom({ position: 'topright' }).addTo(map);
    }
    
    // Click on empty map area hides carousel
    map.on('click', () => {
      setActiveRestaurantId(null);
      setShowCarousel(false);
    });
    
    mapRef.current = map;
    setMapReady(true);
    
    // Invalidate size after render to handle layout shifts
    setTimeout(() => map.invalidateSize(), 100);
    
    return () => {
      map.remove();
      mapRef.current = null;
      clusterGroupRef.current = null;
      userMarkerRef.current = null;
      setMapReady(false);
      if (momentumRef.current) {
        cancelAnimationFrame(momentumRef.current);
      }
    };
  }, []);
  
  // Add markers with clustering
  useEffect(() => {
    if (!mapReady || !mapRef.current) return;
    
    // Clear existing
    if (clusterGroupRef.current) {
      mapRef.current.removeLayer(clusterGroupRef.current);
    }
    markersRef.current = [];
    
    // Create cluster group
    // @ts-ignore
    const clusterGroup = L.markerClusterGroup({
      maxClusterRadius: 50,
      spiderfyOnMaxZoom: true,
      showCoverageOnHover: false,
      zoomToBoundsOnClick: true,
      disableClusteringAtZoom: 16,
      animate: true,
      animateAddingMarkers: false,
      // Mobile: don't spiderfy at small screen
      spiderfyDistanceMultiplier: window.innerWidth < 768 ? 1.5 : 1,
      iconCreateFunction: (cluster: any) => {
        const count = cluster.getChildCount();
        const size = count > 20 ? 52 : count > 10 ? 48 : 44;
        return L.divIcon({
          html: `
            <div style="
              width: ${size}px;
              height: ${size}px;
              background: #009544;
              border-radius: 50%;
              display: flex;
              align-items: center;
              justify-content: center;
              color: white;
              font-weight: 700;
              font-size: ${count > 99 ? 12 : 14}px;
              box-shadow: 0 4px 14px rgba(0,149,68,0.5);
              border: 3px solid white;
            ">
              ${count}
            </div>
          `,
          className: 'marker-cluster-custom',
          iconSize: L.point(size, size),
          iconAnchor: L.point(size / 2, size / 2)
        });
      }
    });
    
    // Add restaurant markers — store restaurant ID on each marker
    sortedRestaurants.forEach((restaurant) => {
      const lat = Number(restaurant.latitude);
      const lng = Number(restaurant.longitude);
      
      const marker = L.marker([lat, lng], {
        icon: createMarkerIcon(activeRestaurantId === restaurant.id)
      });
      
      // Store restaurant ID for precise matching
      (marker as any)._restaurantId = restaurant.id;
      
      marker.on('click', (e) => {
        L.DomEvent.stopPropagation(e);
        setActiveRestaurantId(restaurant.id);
        setShowCarousel(true);
        
        // Scroll reel to this exact restaurant card
        requestAnimationFrame(() => {
          scrollToRestaurant(restaurant.id);
        });
        
        // Center map on marker with offset for carousel
        const targetLat = lat - (mapRef.current ? (mapRef.current.getBounds().getNorth() - mapRef.current.getBounds().getSouth()) * 0.15 : 0);
        mapRef.current?.setView([targetLat, lng], Math.max(mapRef.current?.getZoom() || 15, 15), { animate: true });
      });
      
      clusterGroup.addLayer(marker);
      markersRef.current.push(marker);
    });
    
    clusterGroup.addTo(mapRef.current);
    clusterGroupRef.current = clusterGroup;
    
    // Handle cluster click — zoom in, show carousel with nearest restaurant
    clusterGroup.on('clusterclick', (e: any) => {
      const childMarkers = e.layer.getAllChildMarkers();
      if (childMarkers.length > 0) {
        const firstId = (childMarkers[0] as any)._restaurantId;
        if (firstId) {
          setActiveRestaurantId(firstId);
          setShowCarousel(true);
          setTimeout(() => scrollToRestaurant(firstId), 150);
        }
      }
    });
    
    // Add user location marker
    if (userLocation && !userMarkerRef.current) {
      userMarkerRef.current = L.marker([userLocation.lat, userLocation.lng], {
        icon: createUserLocationIcon(),
        interactive: false,
        zIndexOffset: -1000
      }).addTo(mapRef.current);
    }
    
    // Fit bounds
    if (sortedRestaurants.length > 1 && !activeRestaurantId) {
      const bounds = L.latLngBounds(
        sortedRestaurants.map(r => [Number(r.latitude), Number(r.longitude)] as [number, number])
      );
      if (userLocation) {
        bounds.extend([userLocation.lat, userLocation.lng]);
      }
      mapRef.current.fitBounds(bounds, { 
        padding: [40, 40],
        maxZoom: 15 
      });
    }
  }, [mapReady, sortedRestaurants.length, userLocation]);
  
  // Update marker icons when active changes
  useEffect(() => {
    markersRef.current.forEach((marker) => {
      const id = (marker as any)._restaurantId;
      marker.setIcon(createMarkerIcon(activeRestaurantId === id));
      // Bring active marker to front
      if (activeRestaurantId === id) {
        marker.setZIndexOffset(1000);
      } else {
        marker.setZIndexOffset(0);
      }
    });
  }, [activeRestaurantId]);
  
  // Scroll reel to a restaurant by ID (precise, not index-based)
  const scrollToRestaurant = useCallback((restaurantId: string) => {
    const container = cardsContainerRef.current;
    if (!container) return;
    
    const cardEl = cardRefsMap.current.get(restaurantId);
    if (cardEl) {
      const containerRect = container.getBoundingClientRect();
      const cardRect = cardEl.getBoundingClientRect();
      // Center the card in the container
      const scrollTarget = cardEl.offsetLeft - containerRect.width / 2 + cardRect.width / 2;
      container.scrollTo({ left: Math.max(0, scrollTarget), behavior: 'smooth' });
    }
  }, []);
  
  // Detect which card is centered during scroll (IntersectionObserver approach)
  useEffect(() => {
    const container = cardsContainerRef.current;
    if (!container || !showCarousel) return;
    
    let scrollTimeout: ReturnType<typeof setTimeout>;
    
    const handleScroll = () => {
      if (isDraggingRef.current) return;
      
      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(() => {
        // Find the card closest to center
        const containerRect = container.getBoundingClientRect();
        const centerX = containerRect.left + containerRect.width / 2;
        
        let closestId: string | null = null;
        let closestDist = Infinity;
        
        cardRefsMap.current.forEach((cardEl, id) => {
          const cardRect = cardEl.getBoundingClientRect();
          const cardCenterX = cardRect.left + cardRect.width / 2;
          const dist = Math.abs(cardCenterX - centerX);
          if (dist < closestDist) {
            closestDist = dist;
            closestId = id;
          }
        });
        
        if (closestId && closestId !== activeRestaurantId) {
          setActiveRestaurantId(closestId);
          // Pan map to this restaurant
          const restaurant = sortedRestaurants.find(r => r.id === closestId);
          if (restaurant && mapRef.current) {
            const lat = Number(restaurant.latitude);
            const lng = Number(restaurant.longitude);
            mapRef.current.panTo([lat, lng], { animate: true, duration: 0.4 });
          }
        }
      }, 120);
    };
    
    container.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      container.removeEventListener('scroll', handleScroll);
      clearTimeout(scrollTimeout);
    };
  }, [showCarousel, activeRestaurantId, sortedRestaurants]);
  
  // Mouse drag handlers for desktop carousel with momentum
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if (!cardsContainerRef.current) return;
    e.preventDefault();
    
    if (momentumRef.current) {
      cancelAnimationFrame(momentumRef.current);
      momentumRef.current = null;
    }
    
    isDraggingRef.current = true;
    setIsDragging(true);
    dragStartRef.current = {
      x: e.pageX,
      scrollLeft: cardsContainerRef.current.scrollLeft,
      lastX: e.pageX,
      lastTime: Date.now(),
      velocity: 0
    };
  }, []);
  
  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (!isDraggingRef.current || !cardsContainerRef.current) return;
    e.preventDefault();
    
    const x = e.pageX;
    const now = Date.now();
    const dt = now - dragStartRef.current.lastTime;
    
    if (dt > 0) {
      dragStartRef.current.velocity = (dragStartRef.current.lastX - x) / dt;
    }
    
    dragStartRef.current.lastX = x;
    dragStartRef.current.lastTime = now;
    
    const walk = dragStartRef.current.x - x;
    cardsContainerRef.current.scrollLeft = dragStartRef.current.scrollLeft + walk;
  }, []);
  
  const handleMouseUp = useCallback(() => {
    if (!isDraggingRef.current || !cardsContainerRef.current) return;
    isDraggingRef.current = false;
    setIsDragging(false);
    
    const vel = dragStartRef.current.velocity;
    if (Math.abs(vel) > 0.1) {
      let currentVelocity = vel * 15;
      const friction = 0.95;
      
      const applyMomentum = () => {
        if (!cardsContainerRef.current || Math.abs(currentVelocity) < 0.5) {
          momentumRef.current = null;
          return;
        }
        cardsContainerRef.current.scrollLeft += currentVelocity;
        currentVelocity *= friction;
        momentumRef.current = requestAnimationFrame(applyMomentum);
      };
      
      momentumRef.current = requestAnimationFrame(applyMomentum);
    }
  }, []);
  
  // Close carousel
  const handleCloseCarousel = useCallback(() => {
    setActiveRestaurantId(null);
    setShowCarousel(false);
  }, []);
  
  // Card select - center map on restaurant
  const handleCardSelect = useCallback((restaurant: RestaurantWithDetails) => {
    setActiveRestaurantId(restaurant.id);
    if (mapRef.current) {
      const lat = Number(restaurant.latitude);
      const lng = Number(restaurant.longitude);
      mapRef.current.setView([lat, lng], Math.max(mapRef.current.getZoom(), 15), { animate: true });
    }
  }, []);
  
  // Open restaurant detail page
  const handleOpenDetail = useCallback((restaurant: RestaurantWithDetails) => {
    if (onRestaurantClick) {
      onRestaurantClick(restaurant);
    }
  }, [onRestaurantClick]);
  
  // Open restaurant Speisekarte (menu page)
  const handleOpenMenu = useCallback((restaurant: RestaurantWithDetails) => {
    const slug = restaurant.slug || slugify(restaurant.name);
    // Navigate to restaurant page with menu tab active
    window.location.href = `/restaurants/${slug}#speisekarte`;
  }, []);
  
  // Navigate cards by direction
  const navigateCards = useCallback((direction: 'prev' | 'next') => {
    if (activeIndex === null) return;
    
    const newIndex = direction === 'next' 
      ? Math.min(activeIndex + 1, sortedRestaurants.length - 1)
      : Math.max(activeIndex - 1, 0);
    
    const restaurant = sortedRestaurants[newIndex];
    if (!restaurant) return;
    
    setActiveRestaurantId(restaurant.id);
    scrollToRestaurant(restaurant.id);
    
    if (mapRef.current) {
      mapRef.current.setView(
        [Number(restaurant.latitude), Number(restaurant.longitude)],
        Math.max(mapRef.current.getZoom(), 15),
        { animate: true }
      );
    }
  }, [activeIndex, sortedRestaurants, scrollToRestaurant]);

  // Register card ref
  const setCardRef = useCallback((id: string, el: HTMLDivElement | null) => {
    if (el) {
      cardRefsMap.current.set(id, el);
    } else {
      cardRefsMap.current.delete(id);
    }
  }, []);

  return (
    <div className={cn(
      "relative w-full rounded-2xl overflow-hidden",
      // Better height management for mobile
      "h-[calc(100vh-200px)] min-h-[450px] max-h-[800px]",
      "sm:h-[calc(100vh-250px)]",
      "lg:h-[600px] lg:max-h-none",
      className
    )}>
      {/* Map Container */}
      <div ref={mapContainerRef} className="absolute inset-0 z-0" />
      
      {/* Global styles for Leaflet mobile fixes */}
      <style>{`
        .leaflet-container { 
          touch-action: pan-x pan-y;
          -webkit-tap-highlight-color: transparent;
        }
        .custom-marker { 
          background: transparent !important; 
          border: none !important; 
        }
        .user-location-marker { 
          background: transparent !important; 
          border: none !important; 
        }
        .marker-cluster-custom { 
          background: transparent !important; 
          border: none !important; 
        }
        .leaflet-control-attribution {
          font-size: 9px !important;
          background: rgba(255,255,255,0.6) !important;
          padding: 1px 4px !important;
        }
        @media (max-width: 640px) {
          .leaflet-control-attribution {
            font-size: 8px !important;
            max-width: 150px;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
          }
        }
        .scrollbar-hide::-webkit-scrollbar { display: none; }
        .scrollbar-hide { -ms-overflow-style: none; scrollbar-width: none; }
      `}</style>
      
      {/* Back to List Button - Top Left */}
      {onBackToList && (
        <button
          onClick={onBackToList}
          className="absolute top-3 left-3 z-20 flex items-center gap-2 px-3.5 py-2 bg-white rounded-full shadow-lg hover:shadow-xl transition-all text-sm font-medium text-gray-700 hover:text-[#009544] active:scale-95 touch-manipulation"
        >
          <List className="w-4 h-4" />
          <span className="hidden sm:inline">Zur Liste</span>
        </button>
      )}
      
      {/* Restaurant Cards Carousel */}
      {showCarousel && (
        <div 
          className="absolute bottom-0 left-0 right-0 z-20 pointer-events-none"
          style={{ 
            paddingBottom: 'max(12px, env(safe-area-inset-bottom, 12px))' 
          }}
        >
          {/* Top row: Navigation Counter + Close Button */}
          <div className="flex items-center justify-between px-3 sm:px-4 pb-1.5 pt-2 pointer-events-auto">
            {/* Navigation Counter */}
            {activeIndex !== null && (
              <div className="flex items-center gap-0.5 bg-white rounded-full shadow-md px-1 py-0.5">
                <button
                  onClick={() => navigateCards('prev')}
                  disabled={activeIndex === 0}
                  className="w-7 h-7 sm:w-8 sm:h-8 flex items-center justify-center rounded-full hover:bg-gray-100 disabled:opacity-30 transition-colors touch-manipulation active:scale-90"
                >
                  <ChevronLeft className="w-4 h-4 sm:w-5 sm:h-5" />
                </button>
                <span className="text-xs sm:text-sm font-semibold px-1.5 sm:px-2 min-w-[50px] sm:min-w-[60px] text-center tabular-nums">
                  {activeIndex + 1} / {sortedRestaurants.length}
                </span>
                <button
                  onClick={() => navigateCards('next')}
                  disabled={activeIndex === sortedRestaurants.length - 1}
                  className="w-7 h-7 sm:w-8 sm:h-8 flex items-center justify-center rounded-full hover:bg-gray-100 disabled:opacity-30 transition-colors touch-manipulation active:scale-90"
                >
                  <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5" />
                </button>
              </div>
            )}
            
            {/* Close Button */}
            <button
              onClick={handleCloseCarousel}
              className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 transition-colors ml-auto touch-manipulation active:scale-90"
              title="Schließen"
            >
              <X className="w-4 h-4 text-gray-600" />
            </button>
          </div>
          
          <div className="px-3 sm:px-4 pb-1 pointer-events-auto">
            <div 
              ref={cardsContainerRef}
              className={cn(
                "flex gap-3 overflow-x-auto scrollbar-hide snap-x snap-mandatory py-2",
                isDragging ? "cursor-grabbing" : "cursor-grab"
              )}
              style={{ 
                scrollPaddingLeft: '12px',
                WebkitOverflowScrolling: 'touch',
                scrollBehavior: isDragging ? 'auto' : 'smooth',
                userSelect: isDragging ? 'none' : 'auto',
                overscrollBehaviorX: 'contain',
              }}
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onMouseLeave={handleMouseUp}
            >
              {sortedRestaurants.map((restaurant) => (
                <RestaurantMapCard
                  key={restaurant.id}
                  ref={(el: HTMLDivElement | null) => setCardRef(restaurant.id, el)}
                  restaurant={restaurant}
                  isActive={activeRestaurantId === restaurant.id}
                  distanceKm={restaurantDistances.get(restaurant.id)}
                  onSelect={() => handleCardSelect(restaurant)}
                  onOpenDetail={() => handleOpenDetail(restaurant)}
                  onOpenMenu={() => handleOpenMenu(restaurant)}
                />
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
});

export default RestaurantMapView;
